// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  Returns,
  StockAnalysis,
} from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { ChartContainer } from '@shared/ui-kit/components/charts/chart-container';
import type { PopupsName } from '@shared/ui-kit/components/info-popup/data/popup-names';
import { PopupButton } from '@shared/ui-kit/components/info-popup/popup-button';
import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useSelector } from 'react-redux';
import { selectAccountCurrency } from '@shared/data-access/slices/user/user-selectors';
import { useTranslation } from 'react-i18next';
import { Table, type TableDataProps } from '@shared/ui-kit/components';

interface KeyMetricsTableProps {
  analysis: StockAnalysis;
  visibleMetrics?: string[];
}

export const KeyMetricsTable: FC<KeyMetricsTableProps> = ({
  analysis,
  visibleMetrics = [
    'returns_3M',
    'returns_6M',
    'CAGR',
    'returns_3Y',
    'returns_5Y',
    'beta',
    'volatility',
    'r_square',
    'sharpe',
    'max_drawdown',
    'tracking_error',
  ],
}) => {
  const { t } = useTranslation();

  const currency = useSelector(selectAccountCurrency);

  const currencyRef = useRef(currency);
  const startDate = useRef(new Date(analysis.historical_data.start_date));
  const { returns: analysisReturns, risk, historical_data } = analysis;

  const tracking = 'tracking' in analysis ? analysis.tracking : undefined;

  const [returns, setReturns] = useState<Returns>(analysis.returns);

  const dataKeyMetrics = useMemo(
    () => [
      {
        id: 'returns_YTD',
        label: t('glossary.investment-returns-ytd', { ns: 'glossary' }),
        portfolioValue: returns?.returns_YTD,
        benchmarkValue: returns?.bnch_returns_YTD,
        format: 'percentage',
      },
      {
        id: 'returns_3M',
        label: t('glossary.investment-returns-m3', { ns: 'glossary' }),
        portfolioValue: returns?.returns_3M,
        benchmarkValue: returns?.bnch_returns_3M,
        format: 'percentage',
      },
      {
        id: 'returns_6M',
        label: t('glossary.investment-returns-m6', { ns: 'glossary' }),
        portfolioValue: returns?.returns_6M,
        benchmarkValue: returns?.bnch_returns_6M,
        format: 'percentage',
      },
      {
        id: 'CAGR',
        label: t('glossary.total-1y-returns', { ns: 'glossary' }),
        portfolioValue: returns?.CAGR,
        benchmarkValue: returns?.bnch_CAGR,
        format: 'percentage',
      },
      {
        id: 'returns_3Y',
        label: t('glossary.total-3y-returns', { ns: 'glossary' }),
        portfolioValue: returns?.returns_3Y,
        benchmarkValue: returns?.bnch_returns_3Y,
        format: 'percentage',
      },
      {
        id: 'returns_5Y',
        label: t('glossary.total-5y-returns', { ns: 'glossary' }),
        portfolioValue: returns?.returns_5Y,
        benchmarkValue: returns?.bnch_returns_5Y,
        format: 'percentage',
      },
      {
        id: 'beta',
        label: t('glossary.beta', { ns: 'glossary' }),
        portfolioValue: risk.beta,
        format: 'number',
        popupName: 'kpis.beta' as PopupsName,
      },
      {
        id: 'returns_max',
        label: t('glossary.investment-returns-inception', { ns: 'glossary' }),
        portfolioValue: returns?.returns_max,
        benchmarkValue: returns?.bnch_returns_max,
        format: 'percentage',
      },
      {
        id: 'volatility',
        label: t('glossary.volatility', { ns: 'glossary' }),
        portfolioValue: risk.volatility,
        benchmarkValue: risk.bnch_volatility,
        format: 'percentage',
      },
      {
        id: 'r_square',
        label: t('glossary.r-square', { ns: 'glossary' }),
        portfolioValue: tracking?.r_square,
        format: 'percentage',
        popupName: 'kpis.rsquare' as PopupsName,
      },
      {
        id: 'sharpe',
        label: t('glossary.sharpe-ratio', { ns: 'glossary' }),
        portfolioValue: risk.sharpe,
        format: 'number',
      },
      {
        id: 'max_drawdown',
        label: t('glossary.max-drawdown', { ns: 'glossary' }),
        portfolioValue: risk.max_drawdown,
        benchmarkValue: risk.bnch_max_drawdown,
        format: 'percentage',
        popupName: 'kpis.maxDrawdown' as PopupsName,
      },
      {
        id: 'tracking_error',
        label: t('glossary.tracking-error', { ns: 'glossary' }),
        portfolioValue: tracking?.tracking_error,
        format: 'percentage',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [returns, risk, tracking, t],
  );

  useEffect(() => {
    if (new Date(historical_data.start_date) < startDate.current) {
      startDate.current = new Date(historical_data.start_date);
      setReturns(analysisReturns);
    }
    if (currency !== currencyRef.current) {
      currencyRef.current = currency;
      startDate.current = new Date(historical_data.start_date);
      setReturns(analysisReturns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisReturns, historical_data.start_date]);

  const filteredKeyMetrics = dataKeyMetrics.filter(metric =>
    visibleMetrics.includes(metric.id),
  );

  return (
    <ChartContainer header={t('glossary.key-metrics', { ns: 'glossary' })}>
      <table className="h-full w-full text-primary-medium">
        <thead>
          <tr className="[&>th]:pb-5 [&>th]:font-normal">
            <th></th>
            <th className="text-right uppercase">
              {t('glossary.portfolio', { ns: 'glossary' })}
            </th>
            <th className="text-right uppercase">
              {t('glossary.benchmark', { ns: 'glossary' })}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredKeyMetrics.map(
            ({ label, portfolioValue, format, benchmarkValue, popupName }) => (
              <tr key={label} className="[&>td]:py-2 [&>td]:align-middle">
                <td className="align-middle">
                  <span className="inline-flex items-center">
                    {label}
                    {popupName && (
                      <PopupButton popupName={popupName as PopupsName} tooltip />
                    )}
                  </span>
                </td>
                <td className="text-right">
                  <Table.Data
                    value={portfolioValue}
                    format={format as TableDataProps['format']}
                  />
                </td>
                <td className="text-right">
                  <Table.Data
                    value={benchmarkValue}
                    format={format as TableDataProps['format']}
                  />
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    </ChartContainer>
  );
};
